import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Paged,
  TicketAssignees,
  TicketCommentInput,
  TicketCounts,
  TicketDto,
  TicketGroupDto,
  TicketGroupInput,
  TicketInput,
  TicketListItemDto,
  TicketTypeDto,
  TicketTypeInput,
} from '@app/shared';
import { api } from '../../lib/api';
import type { Query } from '../../lib/api';

export const useTickets = (params: Query) =>
  useQuery({
    queryKey: ['tickets', 'list', params],
    queryFn: () => api.get<Paged<TicketListItemDto>>('/tickets', params),
    placeholderData: (prev) => prev,
  });

export const useTicketCounts = () =>
  useQuery({
    queryKey: ['tickets', 'counts'],
    queryFn: () => api.get<TicketCounts>('/tickets/counts'),
  });

export const useTicket = (id: string | undefined) =>
  useQuery({
    queryKey: ['tickets', 'one', id],
    queryFn: () => api.get<TicketDto>(`/tickets/${id}`),
    enabled: Boolean(id),
    // A form is built from this answer, so never start it from a cached copy.
    staleTime: 0,
    gcTime: 0,
  });

export const useAssignees = (enabled: boolean) =>
  useQuery({
    queryKey: ['tickets', 'assignees'],
    queryFn: () => api.get<TicketAssignees>('/tickets/assignees'),
    enabled,
  });

export const useCustomerTickets = (customerId: string, params: Query) =>
  useQuery({
    queryKey: ['tickets', 'customer', customerId, params],
    queryFn: () => api.get<Paged<TicketListItemDto>>(`/tickets/by-customer/${customerId}`, params),
    placeholderData: (prev) => prev,
  });

/** Link for the browser to download the filtered list as CSV. */
export const ticketExportUrl = (filters: Query) => api.url('/tickets/export', filters);
export const ticketReportUrl = () => api.url('/tickets/report', {});

export const useTicketTypeOptions = (enabled = true) =>
  useQuery({
    queryKey: ['ticket-types', 'options'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/ticket-types/options'),
    select: (r) => r.data,
    enabled,
  });

export const useTicketTypes = (enabled: boolean) =>
  useQuery({
    queryKey: ['ticket-types', 'list'],
    queryFn: () => api.get<{ data: TicketTypeDto[] }>('/ticket-types'),
    select: (r) => r.data,
    enabled,
  });

export const useTicketGroups = (enabled: boolean) =>
  useQuery({
    queryKey: ['ticket-groups'],
    queryFn: () => api.get<{ data: TicketGroupDto[] }>('/ticket-groups'),
    select: (r) => r.data,
    enabled,
  });

/** A ticket change moves the list, the counts and the open ticket. */
function useTicketMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tickets'] }),
  });
}

export const useCreateTicket = () =>
  useTicketMutation((input: TicketInput) => api.post<TicketDto>('/tickets', input));
export const useUpdateTicket = () =>
  useTicketMutation(({ id, input }: { id: string; input: TicketInput }) =>
    api.put<TicketDto>(`/tickets/${id}`, input),
  );
export const useAddComment = () =>
  useTicketMutation(({ id, input }: { id: string; input: TicketCommentInput }) =>
    api.post<TicketDto>(`/tickets/${id}/comments`, input),
  );

function useTypeMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-types'] }),
  });
}
export const useCreateTicketType = () =>
  useTypeMutation((input: TicketTypeInput) => api.post<TicketTypeDto>('/ticket-types', input));
export const useUpdateTicketType = () =>
  useTypeMutation(({ id, input }: { id: string; input: Partial<TicketTypeInput> }) =>
    api.patch<TicketTypeDto>(`/ticket-types/${id}`, input),
  );

function useGroupMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    // Group names appear on tickets and in the assignee lists.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ticket-groups'] }),
        queryClient.invalidateQueries({ queryKey: ['tickets'] }),
      ]),
  });
}
export const useCreateTicketGroup = () =>
  useGroupMutation((input: TicketGroupInput) => api.post<TicketGroupDto>('/ticket-groups', input));
export const useUpdateTicketGroup = () =>
  useGroupMutation(({ id, input }: { id: string; input: TicketGroupInput }) =>
    api.put<TicketGroupDto>(`/ticket-groups/${id}`, input),
  );
